import _sliced_to_array from "@swc/helpers/src/_sliced_to_array.mjs";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Prompt, Text, useDisclosure } from "@components/mantine";
import { AuthenticationEvents } from "@core/authentication";
import { Translation } from "@core/locales";
import { OrganizationSelectors } from "@core/organization";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import ms from "ms";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Locales } from "../../strings/Locales.js";
import { getThrottle } from "../../utils/GetThrottle.js";
import { getFormatedTimeUntilLogout } from "../../utils/GetFormatedTimeUntilLogout.js";
dayjs.extend(duration);
var OPEN_PROMPT_DELAY = ms("25m");
var PROMPT_LOGOUT_DELAY = ms("5m");
// cannot use React.useState for this one since many write are happening too fast
var promptTimerId = -1;
var getIdleLogoutDelay = function(adminUITimeoutInSeconds) {
    return adminUITimeoutInSeconds ? adminUITimeoutInSeconds * 1000 : OPEN_PROMPT_DELAY;
};
var useBroadcastData = function(param) {
    var id = param.id, onChange = param.onChange;
    var generatedId = useMemo(function() {
        return performance.now();
    }, []);
    var broadcastUpdate = function(value) {
        localStorage.setItem(id, JSON.stringify({
            value: value,
            sourceId: generatedId
        }));
    };
    var onLocalStorageUpdate = function(ev) {
        if (ev.key === id && ev.newValue) {
            var parsed = JSON.parse(ev.newValue);
            if ((parsed === null || parsed === void 0 ? void 0 : parsed.sourceId) !== generatedId) {
                onChange();
            }
        }
    };
    useEffect(function() {
        window.addEventListener("storage", onLocalStorageUpdate);
        return function() {
            window.removeEventListener("storage", onLocalStorageUpdate);
        };
    }, []);
    return [
        broadcastUpdate
    ];
};
var dispatchFullLogoutEvent = function() {
    var event = new Event(AuthenticationEvents.FullLogout, {
        bubbles: true
    });
    document.dispatchEvent(event);
};
var ActualIdleLogoutModel = function() {
    var ref = _sliced_to_array(useDisclosure(false), 2), opened = ref[0], ref1 = ref[1], open = ref1.open, close = ref1.close;
    var ref2 = _sliced_to_array(useState(-1), 2), logoutIntervalId = ref2[0], setLogoutIntervalId = ref2[1];
    var ref3 = _sliced_to_array(useState(null), 2), logoutAt = ref3[0], setLogoutAt = ref3[1];
    var adminUITimeoutInSeconds = useSelector(OrganizationSelectors.getOrganization).adminUITimeoutInSeconds;
    var openPromptDelay = getIdleLogoutDelay(adminUITimeoutInSeconds);
    var checkTimer = function() {
        if (logoutAt !== null && Date.now() >= logoutAt) {
            dispatchFullLogoutEvent();
        } else {
            var id = window.setTimeout(checkTimer, 1000);
            setLogoutIntervalId(id);
        }
    };
    var stay = function() {
        close();
        onUserActivity();
        clearTimeout(logoutIntervalId);
        setLogoutAt(null);
    };
    var openModalIfNeeded = function() {
        if (!opened) {
            open();
            setLogoutAt(Date.now() + PROMPT_LOGOUT_DELAY);
        }
    };
    var resetTimer = function() {
        clearTimeout(promptTimerId);
        promptTimerId = window.setTimeout(openModalIfNeeded, openPromptDelay);
    };
    var ref4 = _sliced_to_array(useBroadcastData({
        id: "last-activity",
        onChange: function() {
            if (!document.hasFocus()) {
                stay();
            }
        }
    }), 1), update = ref4[0];
    var onUserActivity = useMemo(function() {
        return getThrottle(function() {
            resetTimer();
            if (document.hasFocus()) {
                update(dayjs().toJSON());
            }
        }, 1000);
    }, []);
    useEffect(function() {
        window.onmousemove = onUserActivity;
        window.onmousedown = onUserActivity; // catches touchscreen presses as well
        window.ontouchstart = onUserActivity; // catches touchscreen swipes as well
        window.onclick = onUserActivity; // catches touchpad clicks as well
        window.onkeydown = onUserActivity;
        window.addEventListener("scroll", onUserActivity, true);
        resetTimer();
    }, []);
    useEffect(function() {
        if (logoutAt !== null) {
            checkTimer();
        }
    }, [
        logoutAt,
        setLogoutIntervalId
    ]);
    var now = dayjs();
    var timeUntilAutomaticLogout = dayjs.duration(dayjs(logoutAt).diff(now));
    return /*#__PURE__*/ _jsxs(Prompt, {
        id: "idle-logout-modal",
        variant: "warning",
        opened: opened,
        title: Locales.format("IdleLogoutPromptTitle"),
        onClose: function() {
            return stay();
        },
        children: [
            /*#__PURE__*/ _jsx(Translation, {
                t: Locales,
                i18nKey: "IdleLogoutPromptDescription",
                options: {
                    time: getFormatedTimeUntilLogout(timeUntilAutomaticLogout)
                },
                children: /*#__PURE__*/ _jsx(Text, {
                    inherit: true,
                    span: true,
                    fw: 500
                })
            }),
            /*#__PURE__*/ _jsxs(Prompt.Footer, {
                children: [
                    /*#__PURE__*/ _jsx(Prompt.CancelButton, {
                        onClick: dispatchFullLogoutEvent,
                        children: Locales.format("IdleLogoutPromptLogout")
                    }),
                    /*#__PURE__*/ _jsx(Prompt.ConfirmButton, {
                        onClick: stay,
                        children: Locales.format("IdleLogoutPromptStay")
                    })
                ]
            })
        ]
    });
};
export var IdleLogoutModal = function() {
    var adminUITimeoutInSeconds = useSelector(OrganizationSelectors.getOrganization).adminUITimeoutInSeconds;
    if (adminUITimeoutInSeconds === undefined) {
        return null;
    }
    return /*#__PURE__*/ _jsx(ActualIdleLogoutModel, {});
};

//# sourceMappingURL=IdleLogoutModal.js.map